import { AppConfig } from '../AppConfig'
import { ApiHelper } from '../common/helpers/ApiHelper'
import { Helper } from '../common/helpers/Helper';
import { Controller } from '../common/Controller';
import { CDPSettingsConfig } from '../CDPSettingsConfig';
import moment from 'moment-timezone';

export class CdpHelper {
  static _cdpSettings = null;
  static _cdpSettingsConfig = null;
  static _workflowsByOrg = null;
  static _crmContactsOptionsDataByOrg = null;
  static _crmAccountFieldDefByOrg = null;
  static _crmInboxesByOrgUser = null;
  static _crmEmailTemplatesByOrgUser = null;
  static CRM_CONTACT_CHART_EXCLUDED_TAGS = ['WHATSAPP_MESSAGE', 'WHATSAPP_CTA_AD']
  static USE_LOCAL_CDP_SETTINGS_CONFIG = false // set true while adding/testing new settings locally

  static async loadSettings(orgId) {
    if (!orgId) { return }
    if (!CdpHelper._cdpSettings) { CdpHelper._cdpSettings = {} }
    if (!CdpHelper._cdpSettingsConfig) { CdpHelper._cdpSettingsConfig = {} }
    if (!CdpHelper._cdpSettings[orgId]) {
      const response = await ApiHelper.callAwait({
        method: 'GET',
        endPoint: ApiHelper.makeUrlPath2({ ms: AppConfig.CDP_MS.ENDPOINT, paths: ['miscs', 'cdp-settings'], queryParams: { orgId, includeconfig: true } }),
      }) || {}

      if (response.items && typeof response.items === 'object') {
        CdpHelper._cdpSettings[orgId] = response.items
        if (response.config) {
          CdpHelper._cdpSettingsConfig[orgId] = response.config
        }
      } else {
        CdpHelper._cdpSettings[orgId] = response
      }
    }
  }

  static getSettingsConfig(orgId) {
    return CdpHelper._cdpSettingsConfig && orgId in CdpHelper._cdpSettingsConfig
      ? CdpHelper._cdpSettingsConfig[orgId]
      : null
  }

  static resolveSettingsConfig(orgId) {
    if (CdpHelper.USE_LOCAL_CDP_SETTINGS_CONFIG) {
      return CDPSettingsConfig
    }
    return CdpHelper.getSettingsConfig(orgId) || CDPSettingsConfig
  }

  static getSettingDef(orgId, settingName) {
    const config = CdpHelper.resolveSettingsConfig(orgId)
    return (config && config.settings && config.settings[settingName]) || null
  }

  static INTERACTION_BASE_OPTIONS = [
    { key: 'NOTE', label: 'NOTE' },
    { key: 'EMAIL', label: 'EMAIL' },
    { key: 'WHATSAPP', label: 'WHATSAPP' },
    { key: 'FORM_SUBMISSION', label: 'FORM_SUBMISSION' },
    { key: 'BEMAIL', label: 'BEMAIL' },
    { key: 'EDIT_CONTACT', label: 'EDIT CONTACT' },
    { key: 'CLICK TO CALL', label: 'CLICK TO CALL' },
    { key: 'CLICK TO CARRIER CALL', label: 'CLICK TO CARRIER CALL' },
    { key: 'CALL DIRECT', label: 'CALL DIRECT' },
    { key: 'CLICK TO WHATSAPP', label: 'CLICK TO WHATSAPP' },
    { key: 'WHATSAPP DIRECT', label: 'WHATSAPP DIRECT' },
  ]
  // Keep in sync with CRM_CONTACT_FIELD_MAPPING_PRIMARY_TARGETS in FormUtils.js
  static CRM_CONTACT_FIELD_MAPPING_BASE_OPTIONS = [
    { key: 'landingPage', label: 'Landing Page' },
    { key: 'utmSource', label: 'UTM Source' },
    { key: 'utmMedium', label: 'UTM Medium' },
    { key: 'utmCampaign', label: 'UTM Campaign' },
    { key: 'utmTerm', label: 'UTM Term' },
    { key: 'utmContent', label: 'UTM Content' },
    { key: 'utmId', label: 'UTM ID' },
    { key: 'utmReferrer', label: 'UTM Referrer' },
    { key: 'slxAdId', label: 'Ad ID' },
    { key: 'clickId', label: 'Click ID' },
    { key: 'clickIdType', label: 'Click ID Type' },
    { key: 'source', label: 'Source' },
    { key: 'company', label: 'Company' },
    { key: 'jobTitle', label: 'Job Title' },
    { key: 'linkedinProfileUrl', label: 'LinkedIn Profile' },
    { key: 'givenname', label: 'First Name' },
    { key: 'familyname', label: 'Last Name' },
  ]

  static workflowsToOptions(workflows) {
    if (!workflows || typeof workflows !== 'object') {
      return []
    }
    const entries = Object.keys(workflows).reduce((list, key) => {
      const workflow = workflows[key]
      if (workflow && typeof workflow === 'object' && workflow.title) {
        list.push({ key, title: workflow.title })
      }
      return list
    }, [])
    const titleCounts = entries.reduce((counts, entry) => {
      counts[entry.title] = (counts[entry.title] || 0) + 1
      return counts
    }, {})
    return entries.map(({ key, title }) => ({
      key,
      label: titleCounts[title] > 1 ? `${title} (${key})` : title,
    }))
  }

  static interactionsToOptions(workflows) {
    const options = [...CdpHelper.INTERACTION_BASE_OPTIONS]
    const seen = new Set(options.map((o) => o.key))
    CdpHelper.workflowsToOptions(workflows).forEach((option) => {
      if (!seen.has(option.key)) {
        options.push(option)
        seen.add(option.key)
      }
    })
    return options
  }

  static getCrmCustomFieldMappingOptions(fieldDef) {
    const fields = fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields
    if (!Array.isArray(fields)) {
      return []
    }
    const seenKeys = new Set()
    const options = []
    fields.forEach((field) => {
      if (!field || !field._uiKey || field._uiKey === 'custom') {
        return
      }
      const key = String(field._uiKey).trim()
      if (!key || seenKeys.has(key)) {
        return
      }
      seenKeys.add(key)
      options.push({
        key,
        label: field.label || field.title || key,
      })
    })
    return options.sort((a, b) => a.label.localeCompare(b.label))
  }

  static mappingFieldsToOptions(customFieldOptions = []) {
    const options = []
    const seen = new Set()
    const appendOptions = (items = []) => {
      (items || []).forEach((item) => {
        const key = item && item.key ? String(item.key).trim() : ''
        if (!key || seen.has(key)) {
          return
        }
        seen.add(key)
        options.push({
          key,
          label: item && item.label ? String(item.label) : key,
        })
      })
    }
    // Primary targets only (matches backend CRM_CONTACT_FIELD_MAPPING_PRIMARY_TARGETS),
    // then org custom field UI keys — not email/phone/friendlyname/tags/state/etc.
    appendOptions(CdpHelper.CRM_CONTACT_FIELD_MAPPING_BASE_OPTIONS)
    appendOptions(customFieldOptions)
    return options.sort((a, b) => a.label.localeCompare(b.label))
  }

  static async fetchWorkflows(orgId) {
    if (!orgId) {
      return {}
    }
    if (!CdpHelper._workflowsByOrg) {
      CdpHelper._workflowsByOrg = {}
    }
    if (orgId in CdpHelper._workflowsByOrg) {
      return CdpHelper._workflowsByOrg[orgId]
    }
    try {
      const response = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['miscs', 'get-workflows'],
        queryParams: { orgId },
      }) || {}
      const workflows = (response.workflows && typeof response.workflows === 'object') ? response.workflows : {}
      CdpHelper._workflowsByOrg[orgId] = workflows
      return workflows
    } catch (err) {
      CdpHelper._workflowsByOrg[orgId] = {}
      return {}
    }
  }

  /**
   * Persist full Dynamo workflow order. Index in orderedIds becomes order 0..n-1.
   * @param {string} orgId
   * @param {string[]} orderedIds - full list of workflow ids (orgId-createdTs)
   */
  static async reorderWorkflows(orgId, orderedIds) {
    if (!orgId || !Array.isArray(orderedIds) || orderedIds.length === 0) {
      throw new Error('ordered workflow ids are required')
    }
    return ApiHelper.callAwait({
      ms: AppConfig.CDP_MS.ENDPOINT,
      method: 'POST',
      paths: ['miscs', 'workflows-reorder'],
      queryParams: { orgId },
      jsonBody: { orgId, ids: orderedIds },
    })
  }

  static async fetchCrmContactsOptionsData(orgId) {
    if (!orgId) {
      return { fieldDef: null, tagBuckets: [] }
    }
    if (!CdpHelper._crmContactsOptionsDataByOrg) {
      CdpHelper._crmContactsOptionsDataByOrg = {}
    }
    if (orgId in CdpHelper._crmContactsOptionsDataByOrg) {
      return CdpHelper._crmContactsOptionsDataByOrg[orgId]
    }
    try {
      const response = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['crmcontacts'],
        queryParams: { orgId, pb: 'fieldDef', includeCustomFields: 'true', limit: 1, 'tags!': 'INTERNAL', _aggTermsLimit: 100 },
      }) || {}
      const data = {
        fieldDef: response.fieldDef || null,
        tagBuckets: (response.stats && response.stats.tags && response.stats.tags.buckets) || [],
      }
      CdpHelper._crmContactsOptionsDataByOrg[orgId] = data
      return data
    } catch (err) {
      const data = { fieldDef: null, tagBuckets: [] }
      CdpHelper._crmContactsOptionsDataByOrg[orgId] = data
      return data
    }
  }

  static async fetchCrmContactFieldDef(orgId) {
    const data = await CdpHelper.fetchCrmContactsOptionsData(orgId)
    return data.fieldDef
  }

  static crmInboxesToOptions(inboxes) {
    const options = [{ key: 'all', label: 'All' }]
    const seen = new Set(['all'])
    ;(inboxes || []).forEach((inbox) => {
      const email = inbox && inbox.email && String(inbox.email).trim()
      if (email && !seen.has(email)) {
        options.push({ key: email, label: email })
        seen.add(email)
      }
    })
    return options
  }

  static crmInboxIdsToOptions(inboxes) {
    const options = []
    const seen = new Set()
    ;(inboxes || []).forEach((inbox) => {
      if (!inbox || inbox.sharedInbox !== 'YES') {
        return
      }
      const id = inbox.id && String(inbox.id).trim()
      if (!id || seen.has(id)) {
        return
      }
      seen.add(id)
      const email = inbox.email && String(inbox.email).trim()
      options.push({ key: id, label: email || id })
    })
    return options
  }

  static crmEmailTemplateIdsToOptions(templates) {
    const options = []
    const seen = new Set()
    ;(templates || []).forEach((template) => {
      if (!template || template.campaignId || template.shared !== 'YES') {
        return
      }
      const id = template.id && String(template.id).trim()
      if (!id || seen.has(id)) {
        return
      }
      seen.add(id)
      const title = template.title && String(template.title).trim()
      options.push({ key: id, label: title || id })
    })
    return options
  }

  static crmContactTagsToOptions(crmContactAttrs) {
    return CdpHelper.crmContactChartTagsToOptions(crmContactAttrs)
  }

  static crmContactChartTagsToOptions(crmContactAttrs, tagBuckets) {
    const excluded = new Set(CdpHelper.CRM_CONTACT_CHART_EXCLUDED_TAGS)
    const seen = new Set()
    const options = []
    const addTag = (tag) => {
      const key = String(tag || '').trim()
      if (!key || seen.has(key) || excluded.has(key)) {
        return
      }
      seen.add(key)
      options.push({ key, label: key })
    }
    Helper.extractAttrs(crmContactAttrs, 'tags').forEach(addTag)
    ;(tagBuckets || []).forEach((bucket) => addTag(bucket && bucket.key))
    options.sort((a, b) => a.label.localeCompare(b.label))
    return options
  }

  static async fetchCrmContactTagStats(orgId) {
    const data = await CdpHelper.fetchCrmContactsOptionsData(orgId)
    return data.tagBuckets
  }

  static staffUsersToEmailOptions(staffUsers) {
    const options = []
    const seen = new Set()
    ;(staffUsers || []).forEach((user) => {
      const email = user && user.email && String(user.email).trim()
      if (!email || seen.has(email)) {
        return
      }
      seen.add(email)
      options.push({ key: email, label: Helper.formatUserEmail(user) })
    })
    return options
  }

  static async fetchCrmInboxes(orgId, staffUserId) {
    if (!orgId || !staffUserId) {
      return []
    }
    const cacheKey = `${orgId}-${staffUserId}`
    if (!CdpHelper._crmInboxesByOrgUser) {
      CdpHelper._crmInboxesByOrgUser = {}
    }
    if (cacheKey in CdpHelper._crmInboxesByOrgUser) {
      return CdpHelper._crmInboxesByOrgUser[cacheKey]
    }
    try {
      const response = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['crminboxs'],
        queryParams: {
          orgId,
          orgIdUserId: `${orgId}-${staffUserId}`,
          includeShared: 'true',
        },
      }) || {}
      const inboxes = Array.isArray(response.items) ? response.items : []
      CdpHelper._crmInboxesByOrgUser[cacheKey] = inboxes
      return inboxes
    } catch (err) {
      CdpHelper._crmInboxesByOrgUser[cacheKey] = []
      return []
    }
  }

  static async fetchCrmEmailTemplates(orgId, staffUserId) {
    if (!orgId || !staffUserId) {
      return []
    }
    const cacheKey = `${orgId}-${staffUserId}`
    if (!CdpHelper._crmEmailTemplatesByOrgUser) {
      CdpHelper._crmEmailTemplatesByOrgUser = {}
    }
    if (cacheKey in CdpHelper._crmEmailTemplatesByOrgUser) {
      return CdpHelper._crmEmailTemplatesByOrgUser[cacheKey]
    }
    try {
      const response = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['crmemailtemplates'],
        queryParams: {
          orgId,
          orgIdUserId: `${orgId}-${staffUserId}`,
          includeShared: 'true',
          limit: 200,
        },
      }) || {}
      const templates = Array.isArray(response.items) ? response.items : []
      CdpHelper._crmEmailTemplatesByOrgUser[cacheKey] = templates
      return templates
    } catch (err) {
      CdpHelper._crmEmailTemplatesByOrgUser[cacheKey] = []
      return []
    }
  }

  static async fetchCrmAccountFieldDef(orgId) {
    if (!orgId) {
      return null
    }
    if (!CdpHelper._crmAccountFieldDefByOrg) {
      CdpHelper._crmAccountFieldDefByOrg = {}
    }
    if (orgId in CdpHelper._crmAccountFieldDefByOrg) {
      return CdpHelper._crmAccountFieldDefByOrg[orgId]
    }
    try {
      const response = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['crmaccounts'],
        queryParams: { orgId, pb: 'fieldDef', includeCustomFields: 'true', limit: 1 },
      }) || {}
      const fieldDef = response.fieldDef || null
      CdpHelper._crmAccountFieldDefByOrg[orgId] = fieldDef
      return fieldDef
    } catch (err) {
      CdpHelper._crmAccountFieldDefByOrg[orgId] = null
      return null
    }
  }

  static walkSettingsFormFields(fields, visit) {
    (fields || []).forEach((field) => {
      if (!field) {
        return
      }
      visit(field)
      const rowFields = field.listConfig && field.listConfig.rowFields
      if (rowFields) {
        rowFields.forEach((rowField) => visit(rowField))
      }
    })
  }

  static collectSettingsDynamicOptions(settingsConfig) {
    const types = new Set()
    const settings = settingsConfig && settingsConfig.settings
    if (!settings || typeof settings !== 'object') {
      return types
    }
    Object.values(settings).forEach(setting => {
      CdpHelper.walkSettingsFormFields(setting && setting.fields, (field) => {
        if (field && typeof field.options === 'string') {
          types.add(field.options)
        }
      })
    })
    return types
  }

  static populateSettingsFieldOptions(settingsConfig, context = {}) {
    const settings = settingsConfig && settingsConfig.settings
    if (!settings || typeof settings !== 'object') {
      return settingsConfig
    }
    Object.values(settings).forEach(setting => {
      CdpHelper.walkSettingsFormFields(setting && setting.fields, (field) => {
        if (!field || Array.isArray(field.options)) {
          return
        }
        if (field.options === 'interactions' && context.interactions !== undefined) {
          field.options = context.interactions
        } else if (field.options === 'crm-contact-fields' && context.crmContactFields !== undefined) {
          field.options = context.crmContactFields
        } else if (field.options === 'crm-contact-preview-fields' && context.crmContactPreviewFields !== undefined) {
          field.options = context.crmContactPreviewFields
        } else if (field.options === 'crm-account-fields' && context.crmAccountFields !== undefined) {
          field.options = context.crmAccountFields
        } else if (field.options === 'crm-contact-states' && context.crmContactStates !== undefined) {
          field.options = context.crmContactStates
        } else if (field.options === 'crm-inboxes' && context.crmInboxes !== undefined) {
          field.options = context.crmInboxes
        } else if (field.options === 'crm-inbox-ids' && context.crmInboxIds !== undefined) {
          field.options = context.crmInboxIds
        } else if (field.options === 'crm-email-template-ids' && context.crmEmailTemplateIds !== undefined) {
          field.options = context.crmEmailTemplateIds
        } else if (field.options === 'staff-emails' && context.staffEmails !== undefined) {
          field.options = context.staffEmails
        } else if (field.options === 'crm-contact-tags' && context.crmContactTags !== undefined) {
          field.options = context.crmContactTags
        } else if (field.options === 'crm-contact-chart-tags' && context.crmContactChartTags !== undefined) {
          field.options = context.crmContactChartTags
        } else if (field.options === 'mapping-fields' && context.mappingFields !== undefined) {
          field.options = context.mappingFields
        }
      })
    })
    return settingsConfig
  }

  static async enrichSettingsFieldOptions(settingsConfig, orgId, enrichOptions = {}) {
    const dynamicOptionsNeeded = CdpHelper.collectSettingsDynamicOptions(settingsConfig)
    const context = {}
    if (dynamicOptionsNeeded.has('interactions')) {
      const workflows = await CdpHelper.fetchWorkflows(orgId)
      context.interactions = CdpHelper.interactionsToOptions(workflows)
    }
    if (dynamicOptionsNeeded.has('crm-contact-fields') || dynamicOptionsNeeded.has('crm-contact-preview-fields') || dynamicOptionsNeeded.has('crm-contact-states') || dynamicOptionsNeeded.has('mapping-fields')) {
      const fieldDef = await CdpHelper.fetchCrmContactFieldDef(orgId)
      const { CRMHelper } = require('./CRMHelper')
      if (dynamicOptionsNeeded.has('crm-contact-fields') || dynamicOptionsNeeded.has('crm-contact-preview-fields')) {
        const crmContactFields = CRMHelper.getCrmFieldOptions(
          fieldDef,
          [...(CRMHelper.getUtmFields() || []), ...(CRMHelper.getContactInfoExtraFields({ isLeadRoutingEnabled: true }) || [])]
        ) || []
        if (dynamicOptionsNeeded.has('crm-contact-fields')) {
          context.crmContactFields = crmContactFields
        }
        if (dynamicOptionsNeeded.has('crm-contact-preview-fields')) {
          const composite = CRMHelper.getCrmContactPreviewCompositeFieldOptions()
          const compositeKeys = new Set(composite.map((o) => o.key))
          context.crmContactPreviewFields = [
            ...composite,
            ...crmContactFields.filter((o) => o && !compositeKeys.has(o.key)),
          ]
        }
      }
      if (dynamicOptionsNeeded.has('crm-contact-states')) {
        context.crmContactStates = CRMHelper.getCrmContactStateOptions(fieldDef, orgId) || []
      }
      if (dynamicOptionsNeeded.has('mapping-fields')) {
        context.mappingFields = CdpHelper.mappingFieldsToOptions(
          CdpHelper.getCrmCustomFieldMappingOptions(fieldDef)
        )
      }
    }
    if (dynamicOptionsNeeded.has('crm-account-fields')) {
      const fieldDef = await CdpHelper.fetchCrmAccountFieldDef(orgId)
      const { CRMHelper } = require('./CRMHelper')
      context.crmAccountFields = CRMHelper.getCrmFieldOptions(fieldDef) || []
    }
    if (dynamicOptionsNeeded.has('crm-inboxes') || dynamicOptionsNeeded.has('crm-inbox-ids')) {
      const inboxes = await CdpHelper.fetchCrmInboxes(orgId, enrichOptions.staffUserId)
      if (dynamicOptionsNeeded.has('crm-inboxes')) {
        context.crmInboxes = CdpHelper.crmInboxesToOptions(inboxes)
      }
      if (dynamicOptionsNeeded.has('crm-inbox-ids')) {
        context.crmInboxIds = CdpHelper.crmInboxIdsToOptions(inboxes)
      }
    }
    if (dynamicOptionsNeeded.has('crm-email-template-ids')) {
      const templates = await CdpHelper.fetchCrmEmailTemplates(orgId, enrichOptions.staffUserId)
      context.crmEmailTemplateIds = CdpHelper.crmEmailTemplateIdsToOptions(templates)
    }
    if (dynamicOptionsNeeded.has('staff-emails')) {
      context.staffEmails = CdpHelper.staffUsersToEmailOptions(enrichOptions.staffUsers)
    }
    if (dynamicOptionsNeeded.has('crm-contact-tags')) {
      context.crmContactTags = CdpHelper.crmContactTagsToOptions(enrichOptions.crmContactAttrs)
    }
    if (dynamicOptionsNeeded.has('crm-contact-chart-tags')) {
      const tagBuckets = await CdpHelper.fetchCrmContactTagStats(orgId)
      context.crmContactChartTags = CdpHelper.crmContactChartTagsToOptions(enrichOptions.crmContactAttrs, tagBuckets)
    }
    return CdpHelper.populateSettingsFieldOptions(settingsConfig, context)
  }

  static getSetting(key, orgId) {
    return CdpHelper._cdpSettings && orgId in CdpHelper._cdpSettings ? CdpHelper._cdpSettings[orgId][key] : null
  }

  static getAllSettings(orgId) {
    return CdpHelper._cdpSettings && orgId in CdpHelper._cdpSettings ? CdpHelper._cdpSettings[orgId] : null
  }

  static patchDripCampaignTracking(campaign) {
    if (campaign && campaign.iType === 'DRIP') {
      const doEmailTracking = campaign.doEmailTracking
      if (!('doOpenTracking' in campaign)) {
        campaign.doOpenTracking = doEmailTracking ? doEmailTracking : 'ENABLED'
      }
      if (!('doClickTracking' in campaign)) {
        campaign.doClickTracking = doEmailTracking ? doEmailTracking : 'ENABLED'
      }
    }
  }

  /** Unique-lead executeStats UI only when BE stamped version 1 (not mere presence of executeStats). */
  static isNewDripExecuteStats(campaign) {
    return !!(campaign && campaign.executeStatsVersion === 1)
  }

  static filterOutDeletedCampaigns(items) {
    return (items || []).filter(c => c && c.state !== 'DELETED')
  }

  static getEnrolmentSource(enrolment) {
    const source = enrolment && enrolment.enrolmentSource
    if (!source || source === 'MANUAL') {
      return 'STAFF'
    }
    return source
  }

  /** Path under baseRoute (e.g. /admin/orgs/:orgId) when enrolmentSourceId is linkable. */
  static getEnrolmentSourcePath(enrolment, baseRoute) {
    const sourceId = enrolment && enrolment.enrolmentSourceId
    if (!sourceId || !baseRoute) {
      return null
    }
    const source = CdpHelper.getEnrolmentSource(enrolment)
    if (source === 'STAFF') {
      return `${baseRoute}/staff-activity/${sourceId}`
    }
    if (source === 'RULE') {
      return `${baseRoute}/automations/${sourceId}`
    }
    return null
  }

  static getDripActivityChartData(points, key, label, color) {
    return {
      labels: (points || []).map(point => point.label),
      datasets: [
        {
          label,
          data: (points || []).map(point => point[key] || 0),
          borderWidth: 2,
          borderColor: color,
          backgroundColor: color,
          tension: 0.3,
        }
      ]
    }
  }

  static getDripEngagementChartData(points) {
    const series = [
      { key: 'firstSend', label: 'Outreaches', color: '#A9E3FF', alwaysInclude: true },
      { key: 'reply', label: 'Replies', color: '#FFE1EE' },
      { key: 'open', label: 'Opens', color: '#FFD7BB' },
      { key: 'click', label: 'Clicks', color: '#3BDE86' },
      { key: 'send', label: 'All Sends', color: '#C5B8FF' },
    ]
    const labels = (points || []).map(p => p.label)
    const datasets = series
      .filter(s => s.alwaysInclude || (points || []).some(p => (p[s.key] || 0) > 0))
      .map(s => ({
        label: s.label,
        data: (points || []).map(p => p[s.key] || 0),
        borderWidth: 2,
        borderColor: s.color,
        backgroundColor: s.color,
        tension: 0.3,
        fill: false,
      }))
    return { labels, datasets }
  }

  static hasChartSeriesData(chartData) {
    const datasets = (chartData && chartData.datasets) || []
    return datasets.some(dataset =>
      (dataset.data || []).some(value => (value || 0) > 0)
    )
  }

  static buildEmptyDripActivityPoints(timezone, days = 7) {
    const points = []
    for (let i = days - 1; i >= 0; i--) {
      const dayMoment = moment().tz(timezone).startOf('day').subtract(i, 'days')
      points.push({
        date: dayMoment.format('YYYY-MM-DD'),
        label: dayMoment.format('DD MMM'),
        firstSend: 0,
        reply: 0,
        open: 0,
        click: 0,
        send: 0,
      })
    }
    return points
  }

  /**
   * 30d FIRST_SEND + REPLY + OPEN + CLICK + SEND timeseries from aggregate-tsanalytic.
   * Pass campaignId for a single drip; omit for org-wide across all drips.
   */
  static async fetchDripActivityCharts({ orgId, campaignId } = {}) {
    const emptyCharts = {
      engagementChartData: CdpHelper.getDripEngagementChartData([]),
    }
    if (!orgId) {
      return emptyCharts
    }
    const days = 30
    const timezone = Helper.getLocalTimezone()
    const startMoment = moment().tz(timezone).startOf('day').subtract(days - 1, 'days')
    const endMoment = moment().tz(timezone).endOf('day')
    const gteCreatedTs = startMoment.valueOf()
    const lteCreatedTs = endMoment.valueOf()
    const pointsByDate = {}
    CdpHelper.buildEmptyDripActivityPoints(timezone, days).forEach(point => {
      pointsByDate[point.date] = point
    })

    try {
      const jsonBody = {
        groupId: 'STAFF',
        iType: 'ACTIVITY',
        orgId,
        activityType: 'FIRST_SEND,REPLY,OPEN,CLICK,SEND',
        sourceType: 'CAMPAIGN',
        sourceSubType: 'DRIP',
        sourceChannel: 'EMAIL',
        gteCreatedTs,
        lteCreatedTs,
        timeZone: timezone,
        aggs: [
          {
            name: 'byDay',
            type: 'date_histogram',
            field: 'createdTs',
            interval: 'day',
            format: 'yyyy-MM-dd',
            minDocCount: 0,
            extendedBounds: {
              min: gteCreatedTs,
              max: lteCreatedTs,
            },
            aggs: [
              {
                name: 'byActivityType',
                type: 'terms',
                field: 'activityType',
                size: 20,
              },
            ],
          },
        ],
      }
      if (campaignId) {
        jsonBody.sourceId = campaignId
      }
      const result = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'POST',
        paths: ['miscs', 'aggregate-tsanalytic'],
        queryParams: { orgId },
        jsonBody,
      }) || {}
      const dayBuckets = (((result.stats || {}).byDay || {}).buckets) || []
      dayBuckets.forEach(bucket => {
        const date = bucket && bucket.key_as_string
        if (!date || !pointsByDate[date]) return
        const activityBuckets = (((bucket.byActivityType || {}).buckets) || [])
        let firstSend = 0
        let reply = 0
        let open = 0
        let click = 0
        let send = 0
        activityBuckets.forEach(activityBucket => {
          if (!activityBucket) return
          if (activityBucket.key === 'FIRST_SEND') {
            firstSend = activityBucket.doc_count || 0
          } else if (activityBucket.key === 'REPLY') {
            reply = activityBucket.doc_count || 0
          } else if (activityBucket.key === 'OPEN') {
            open = activityBucket.doc_count || 0
          } else if (activityBucket.key === 'CLICK') {
            click = activityBucket.doc_count || 0
          } else if (activityBucket.key === 'SEND') {
            send = activityBucket.doc_count || 0
          }
        })
        pointsByDate[date].firstSend = firstSend
        pointsByDate[date].reply = reply
        pointsByDate[date].open = open
        pointsByDate[date].click = click
        pointsByDate[date].send = send
      })

      const points = CdpHelper.buildEmptyDripActivityPoints(timezone, days).map(emptyPoint => pointsByDate[emptyPoint.date] || emptyPoint)
      return {
        engagementChartData: CdpHelper.getDripEngagementChartData(points),
      }
    } catch (err) {
      return emptyCharts
    }
  }

  static generateCustomFields(fieldDef, placement) {
    const fields = []
    const operators = [{ key: 'equals', label: 'equals' },{ key: 'not equals', label: 'not equals' }]

    if (!fieldDef || !fieldDef.steps || !fieldDef.steps[0] || !Array.isArray(fieldDef.steps[0].fields)) {
      return fields
    }

    fieldDef.steps[0].fields.forEach(f => {
      const filterKey = (f.key && String(f.key).startsWith('account.')) ? f.key : f._uiKey
      if (!filterKey) return
      if (['radio', 'check_box'].includes(f.type)) {
        const field = JSON.parse(JSON.stringify(f))
        field.key = filterKey
        field.operators = operators
        field.defaultOperator = 'equals'
        field.placement = placement
        fields.push(field)
        placement = placement === 'left' ? 'right' : 'left'
      } else if (['edit_text'].includes(f.type) && 'v_float' in f) {
        const field = JSON.parse(JSON.stringify(f))
        field.key = filterKey
        if (Array.isArray(field.options) && field.options.length > 0) {
          field.type = 'check_box'
          field.multiSelect = true
          field.operators = operators
          field.defaultOperator = 'equals'
        } else {
          field.type = 'numberRange'
        }
        field.placement = placement
        fields.push(field)
        placement = placement === 'left' ? 'right' : 'left'
      } else if (['date_yyyymmdd', 'dateTime'].includes(f.type)) {
        const field = JSON.parse(JSON.stringify(f))
        field.key = filterKey
        field.type = 'dateRange'
        field.subType = f.type
        field.operators = operators
        field.defaultOperator = 'equals'
        field.availableDateRanges = ['due', 'overdue', 'upcoming', 'next_7d', 'today', 'yesterday', 'this_week', 'this_month', 'last_7d', 'last_30d', 'last_90d', 'last_6m', 'last_12m', 'current_7d', 'current_30d', 'custom', 'all_time']
        field.timezone = Helper.getLocalTimezone()
        field.placement = placement
        fields.push(field)

        placement = placement === 'left' ? 'right' : 'left'
      }
    })

    return fields
  }

  /** Enrich NUMBER account filter options with from/to from stats range buckets when missing. */
  static enrichAccountFieldDefRangeOptions(accountFieldDef, stats) {
    if (!accountFieldDef || !accountFieldDef.steps || !accountFieldDef.steps[0] || !Array.isArray(accountFieldDef.steps[0].fields)) {
      return accountFieldDef
    }
    const enriched = JSON.parse(JSON.stringify(accountFieldDef))
    enriched.steps[0].fields.forEach((field) => {
      if (!(field && field.type === 'edit_text' && field.v_float && Array.isArray(field.options))) return
      const buckets = stats && stats[field.key] && Array.isArray(stats[field.key].buckets) ? stats[field.key].buckets : []
      if (!buckets.length) return
      field.options = field.options.map((opt) => {
        if (!opt || opt.from != null || opt.to != null) return opt
        const bucket = buckets.find((b) => b && String(b.key) === String(opt.key))
        if (!bucket) return opt
        return {
          ...opt,
          ...(bucket.from != null && bucket.from !== '' ? { from: Number(bucket.from) } : {}),
          ...(bucket.to != null && bucket.to !== '' ? { to: Number(bucket.to) } : {}),
        }
      })
    })
    return enriched
  }

  static generateCustomFieldsForBulkUpdate(fieldDef, placement) {
    const fields = []
    const sourceFields = fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields
    if (!Array.isArray(sourceFields)) {
      return fields
    }

    sourceFields.forEach(f => {
      if (!f || !f._uiKey || f._uiKey === 'custom') {
        return
      }
      const isSelect = ['radio', 'check_box'].includes(f.type)
      const isNumber = f.type === 'edit_text' && 'v_float' in f
      const isDate = ['date_yyyymmdd', 'dateTime'].includes(f.type)
      if (!isSelect && !isNumber && !isDate) {
        return
      }
      const field = JSON.parse(JSON.stringify(f))
      field.key = f._uiKey
      field.placement = placement
      fields.push(field)
      placement = placement === 'left' ? 'right' : 'left'
    })

    return fields
  }

  static parseCDPArrayOfObjectsString(input) {
    if (typeof input !== 'string' || !input.trim()) return [];

    return input
      .split('|')
      .map(item => item.trim())
      .filter(Boolean)
      .map(item => {
        return item
          .split(';')
          .map(field => field.trim())
          .filter(Boolean)
          .reduce((obj, field) => {
            const separatorIndex = field.indexOf(':');
            if (separatorIndex === -1) return obj;

            const key = field.slice(0, separatorIndex).trim();
            let value = field.slice(separatorIndex + 1).trim();

            if (key) {
              if (value === 'true') value = true
              else if (value === 'false') value = false
              obj[key] = value;
            }
            return obj;
          }, {});
      });
  }

  static setSetting(options) {
    if (!options || !options.orgId || !options.settingsKey) {
      return Promise.resolve({ success: false });
    }
    const previousKey = options.previousKey
    const previousValue = options.previousValue
    const settingsKey = options.settingsKey ? options.settingsKey.trim() : options.settingsKey
    const jsonBody = { orgId: options.orgId, settingsKey, previousKey, previousValue }

    if (options.settingsValue !== null && options.settingsValue !== undefined) {
      jsonBody.settingsValue = String(options.settingsValue).trim();
    }

    return ApiHelper.callAwait({
      method: 'POST',
      jsonBody,
      endPoint: ApiHelper.makeUrlPath2({ ms: AppConfig.CDP_MS.ENDPOINT, paths: ['miscs', 'update-cdp-settings'], queryParams: { orgId: options.orgId } }),
    }).then(() => {
      if (CdpHelper._cdpSettings && CdpHelper._cdpSettings[options.orgId]) {
        if (options.settingsValue === null || options.settingsValue === undefined) {
          delete CdpHelper._cdpSettings[options.orgId][options.settingsKey];
        } else {
          CdpHelper._cdpSettings[options.orgId][options.settingsKey] = jsonBody.settingsValue;
        }
      }
      return { success: true };
    }).catch(err => {
      return { success: false, error: Helper.getErrorMsg(err) };
    })
  }
}